import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { NumericInput } from '../../components/NumericInput';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { TabsContent } from '../../components/ui/tabs';
import { Plus, Trash2, Edit, Printer, Download, AlertCircle, Info, Loader2 } from 'lucide-react';
import { QRPreviewModal } from '../shared/WizardComponents';

export const CWPanelListSection = ({
  // Data
  panelList,
  curtainwallTypes,
  glassTypes,
  metalPanelTypes,
  curtainwallElevations,
  selectedPanelListIds,
  newPanel,
  editingPanel,
  showPanelDialog,
  uploadingPanelExcel,
  showPanelExcelDialog,
  panelExcelPreview,
  cwScheduleSearch,
  cwScheduleFilters,
  cwScheduleSortBy,
  operationLoading,
  // Permissions
  canEditCSIItems,
  // Handlers
  setNewPanel,
  setShowPanelDialog,
  setShowPanelExcelDialog,
  setPanelExcelPreview,
  setCwScheduleSearch,
  setCwScheduleFilters,
  setCwScheduleSortBy,
  selectAllItems,
  confirmBulkDelete,
  toggleItemSelection,
  handleCreatePanel,
  handleEditPanel,
  handleDeletePanel,
  handlePanelExcelUpload,
  handlePrintAllPanelLabels,
  handlePrintPanelLabel,
  handlePrintPanelQR,
  handleUpdatePanelPreviewRow,
  handlePanelBulkImport,
  onOpenDetail,
}) => {
  const [qrModal, setQrModal] = useState({ open: false, qrToken: null, label: '' });

  return (
    <TabsContent value="cw-panel-list">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Schedules</h2>
        <div className="flex gap-2">
          {canEditCSIItems && curtainwallTypes.length === 0 && (
            <div className="text-sm text-slate-500 flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              Create a Curtainwall Type first to add schedules
            </div>
          )}
          {canEditCSIItems && curtainwallTypes.length > 0 && (
            <>
              {panelList.length > 0 && (
                <>
                  <Button variant="outline" onClick={() => selectAllItems('panelList')}>
                    {selectedPanelListIds.length === panelList.length ? 'Deselect All' : 'Select All'}
                  </Button>
                  <Button 
                    onClick={() => confirmBulkDelete('panelList')}
                    disabled={selectedPanelListIds.length === 0}
                    className="bg-red-600 hover:bg-red-700 text-white disabled:bg-red-300"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Selected ({selectedPanelListIds.length})
                  </Button>
                </>
              )}
              <Button 
                variant="outline"
                onClick={handlePrintAllPanelLabels}
                disabled={panelList.length === 0}
              >
                <Printer className="h-4 w-4 mr-2" />
                Print All QR Labels
              </Button>
              <Button 
                variant="outline"
                onClick={() => document.getElementById('panel-excel-upload').click()}
                disabled={uploadingPanelExcel}
              >
                <Download className="h-4 w-4 mr-2" />
                {uploadingPanelExcel ? 'Uploading...' : 'Import from Excel'}
              </Button>
              <input
                id="panel-excel-upload"
                type="file"
                accept=".xlsx,.xls"
                onChange={handlePanelExcelUpload}
                style={{ display: 'none' }}
              />
              <Dialog open={showPanelDialog} onOpenChange={setShowPanelDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="add-cw-schedule-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Schedule
                  </Button>
                </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingPanel ? 'Edit Schedule' : 'Add Schedule'}</DialogTitle>
                <DialogDescription>{editingPanel ? 'Update schedule entry' : 'Create a new schedule entry and assign it a curtainwall type'}</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreatePanel} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Label *</Label>
                  <Input data-testid="cw-panel-label-input" value={newPanel.label} onChange={(e) => setNewPanel({...newPanel, label: e.target.value})} placeholder="e.g., CW-001" required />
                </div>
                <div className="space-y-2">
                  <Label>Curtainwall Type *</Label>
                  <Select value={newPanel.curtainwallTypeId} onValueChange={(value) => setNewPanel({...newPanel, curtainwallTypeId: value})}>
                    <SelectTrigger data-testid="cw-panel-type-select">
                      <SelectValue placeholder="Select curtainwall type" />
                    </SelectTrigger>
                    <SelectContent>
                      {curtainwallTypes.map((cw) => (
                        <SelectItem key={cw.id} value={cw.id}>{cw.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Input data-testid="cw-panel-location-input" value={newPanel.location} onChange={(e) => setNewPanel({...newPanel, location: e.target.value})} placeholder="e.g., Floor 2, Bay 3" />
                </div>
                <Button type="submit" className="w-full" data-testid="cw-panel-submit" disabled={operationLoading}>
                  {operationLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {editingPanel ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    editingPanel ? 'Update Schedule' : 'Create Schedule'
                  )}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
            </>
          )}
        </div>
      </div>

      {panelList.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-slate-600">
              {curtainwallTypes.length === 0 ? 'Create curtainwall types first' : 
               (glassTypes.length === 0 && metalPanelTypes.length === 0) ? 'Create glass or metal types first' :
               'No panels created yet'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Filter Section */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1">
              <Input
                type="text"
                placeholder="Search by label..."
                value={cwScheduleSearch}
                onChange={(e) => setCwScheduleSearch(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Select
                value={cwScheduleFilters.type}
                onValueChange={(value) => setCwScheduleFilters({ ...cwScheduleFilters, type: value === 'all' ? '' : value })}
              >
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filter by Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {curtainwallTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={cwScheduleSortBy}
                onValueChange={(value) => setCwScheduleSortBy(value)}
              >
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="createdAt_desc">Newest First</SelectItem>
                  <SelectItem value="createdAt_asc">Oldest First</SelectItem>
                  <SelectItem value="label_asc">Label (A-Z)</SelectItem>
                  <SelectItem value="label_desc">Label (Z-A)</SelectItem>
                  <SelectItem value="type_asc">Type (A-Z)</SelectItem>
                  <SelectItem value="type_desc">Type (Z-A)</SelectItem>
                  <SelectItem value="quantity_desc">Quantity (High-Low)</SelectItem>
                  <SelectItem value="quantity_asc">Quantity (Low-High)</SelectItem>
                </SelectContent>
              </Select>
              <Input
                type="text"
                placeholder="Filter by location..."
                value={cwScheduleFilters.location}
                onChange={(e) => setCwScheduleFilters({ ...cwScheduleFilters, location: e.target.value })}
                className="w-48"
              />
              <Button
                variant="outline"
                onClick={() => {
                  setCwScheduleSearch('');
                  setCwScheduleFilters({ type: '', location: '' });
                  setCwScheduleSortBy('createdAt_desc');
                }}
              >
                Clear
              </Button>
            </div>
          </div>
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    {canEditCSIItems && (
                      <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase w-10"></th>
                    )}
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Label</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Lites</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Dimensions</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Location</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-slate-500 uppercase">QR</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-slate-500 uppercase">
                      <div className="flex items-center justify-center gap-1">
                        Quantity ({panelList.reduce((total, panel) => {
                          let panelMarkerCount = 0;
                          curtainwallElevations.forEach(elevation => {
                            if (elevation.markers && Array.isArray(elevation.markers)) {
                              panelMarkerCount += elevation.markers.filter(m => m.windowListId === panel.id).length;
                            }
                          });
                          return total + panelMarkerCount;
                        }, 0)})
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="h-3.5 w-3.5 text-slate-400 hover:text-slate-600 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Type Usage</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </th>
                    {canEditCSIItems && (
                      <th className="px-3 py-2 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {[...panelList]
                    .filter(panel => {
                      // Search filter
                      if (cwScheduleSearch) {
                        const searchLower = cwScheduleSearch.toLowerCase();
                        if (!panel.label?.toLowerCase().includes(searchLower)) return false;
                      }
                      // Type filter
                      if (cwScheduleFilters.type && panel.curtainwallTypeId !== cwScheduleFilters.type) return false;
                      // Location filter
                      if (cwScheduleFilters.location) {
                        const locationLower = cwScheduleFilters.location.toLowerCase();
                        if (!panel.location?.toLowerCase().includes(locationLower)) return false;
                      }
                      return true;
                    })
                    .sort((a, b) => {
                    switch (cwScheduleSortBy) {
                      case 'label_asc':
                        return (a.label || '').localeCompare(b.label || '');
                      case 'label_desc':
                        return (b.label || '').localeCompare(a.label || '');
                      case 'type_asc':
                        const typeA = curtainwallTypes.find(t => t.id === a.curtainwallTypeId)?.name || '';
                        const typeB = curtainwallTypes.find(t => t.id === b.curtainwallTypeId)?.name || '';
                        return typeA.localeCompare(typeB);
                      case 'type_desc':
                        const typeA2 = curtainwallTypes.find(t => t.id === a.curtainwallTypeId)?.name || '';
                        const typeB2 = curtainwallTypes.find(t => t.id === b.curtainwallTypeId)?.name || '';
                        return typeB2.localeCompare(typeA2);
                      case 'quantity_asc':
                        const qtyA = curtainwallElevations.reduce((count, elev) => count + (elev.markers?.filter(m => m.windowListId === a.id).length || 0), 0);
                        const qtyB = curtainwallElevations.reduce((count, elev) => count + (elev.markers?.filter(m => m.windowListId === b.id).length || 0), 0);
                        return qtyA - qtyB;
                      case 'quantity_desc':
                        const qtyA2 = curtainwallElevations.reduce((count, elev) => count + (elev.markers?.filter(m => m.windowListId === a.id).length || 0), 0);
                        const qtyB2 = curtainwallElevations.reduce((count, elev) => count + (elev.markers?.filter(m => m.windowListId === b.id).length || 0), 0);
                        return qtyB2 - qtyA2;
                      case 'createdAt_asc':
                        return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
                      case 'createdAt_desc':
                      default:
                        return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
                    }
                  }).map((panel) => {
                    const cwType = curtainwallTypes.find(cw => cw.id === panel.curtainwallTypeId);
                    const glassType = panel.panelType === 'GL' ? glassTypes.find(gl => gl.id === panel.glassTypeId) : null;
                    const metalType = panel.panelType === 'MT' ? metalPanelTypes.find(mt => mt.id === panel.metalTypeId) : null;
                    const liteCount = Array.isArray(panel.lites) ? panel.lites.length : 0;
                    
                    return (
                      <tr key={panel.id} className={`hover:bg-slate-50 ${selectedPanelListIds.includes(panel.id) ? 'bg-blue-50' : ''}`}>
                        {canEditCSIItems && (
                          <td className="px-3 py-2">
                            <Checkbox
                              checked={selectedPanelListIds.includes(panel.id)}
                              onCheckedChange={(checked) => toggleItemSelection(panel.id, 'panelList', { target: { checked } })}
                            />
                          </td>
                        )}
                        <td
                          className="px-3 py-2 font-medium text-blue-600 hover:text-blue-800 cursor-pointer"
                          onClick={() => onOpenDetail && onOpenDetail(panel)}
                          data-testid={`cw-panel-label-${panel.id}`}
                        >
                          {panel.label}
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-sm flex-shrink-0" 
                              style={{ backgroundColor: cwType?.markerColor || '#39FF14' }}
                            />
                            {cwType?.name || 'N/A'}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          {liteCount > 0 ? (
                            <span className="px-1.5 py-0.5 rounded text-xs bg-slate-100 text-slate-700">
                              {liteCount} {liteCount === 1 ? 'lite' : 'lites'}
                            </span>
                          ) : panel.panelType ? (
                            <span className={`px-1.5 py-0.5 rounded text-xs ${panel.panelType === 'GL' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                              {panel.panelType === 'GL' ? `Glass: ${glassType?.name || '?'}` : `Metal: ${metalType?.name || '?'}`}
                            </span>
                          ) : (
                            <span className="text-slate-400 text-xs">-</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          {panel.width_in && panel.height_in ? `${panel.width_in}" × ${panel.height_in}"` : '-'}
                        </td>
                        <td className="px-3 py-2 text-slate-600">{panel.location || '-'}</td>
                        <td className="px-3 py-2 text-center">
                          {panel.qrCodeUrl ? (
                            <button 
                              type="button"
                              className="cursor-pointer hover:opacity-70 transition-opacity inline-block"
                              onClick={() => setQrModal({ open: true, qrToken: panel.qrToken, label: panel.label })}
                              title="Click to enlarge QR code"
                              data-testid={`qr-thumbnail-${panel.id}`}
                            >
                              <img 
                                src={panel.qrCodeUrl} 
                                alt={`QR ${panel.label}`}
                                className="w-8 h-8 mx-auto"
                              />
                            </button>
                          ) : (
                            <span className="text-slate-400 text-xs">-</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center text-slate-600">
                          {(() => {
                            let markerCount = 0;
                            curtainwallElevations.forEach(elevation => {
                              if (elevation.markers && Array.isArray(elevation.markers)) {
                                markerCount += elevation.markers.filter(m => m.windowListId === panel.id).length;
                              }
                            });
                            return markerCount > 0 ? markerCount : '-';
                          })()}
                        </td>
                        {canEditCSIItems && (
                          <td className="px-3 py-2 text-right">
                            <div className="flex gap-1 justify-end">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={() => handlePrintPanelLabel(panel.id)}
                                title="Print Label"
                              >
                                <Printer className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={() => handleEditPanel(panel)}
                              >
                                <Edit className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-red-500 hover:bg-red-600 hover:text-white"
                                onClick={() => handleDeletePanel(panel.id)}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
        </>
      )}


      <QRPreviewModal
        open={qrModal.open}
        onClose={() => setQrModal({ open: false, qrToken: null, label: '' })}
        qrToken={qrModal.qrToken}
        productName={qrModal.label}
      />

      {/* Panel Excel Import Dialog */}
      <Dialog open={showPanelExcelDialog} onOpenChange={setShowPanelExcelDialog}>
        <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import Panels from Excel</DialogTitle>
            <DialogDescription>
              AI detected labels and dimensions. Select CW type and panel type for each row.
            </DialogDescription>
          </DialogHeader>
          
          {panelExcelPreview && (
            <div className="mt-4">
              {panelExcelPreview.columnMapping && (
                <div className="mb-4 p-3 bg-blue-50 rounded text-sm">
                  <p className="font-semibold mb-2">AI Detected Columns:</p>
                  <ul className="space-y-1 text-slate-700">
                    <li>• <strong>Labels:</strong> {panelExcelPreview.columnMapping.labelColumn || 'Not found'}</li>
                    <li>• <strong>Dimensions:</strong> {panelExcelPreview.columnMapping.dimensionsColumn || 'Not found'}</li>
                    <li>• <strong>Location:</strong> {panelExcelPreview.columnMapping.locationColumn || 'Not found'}</li>
                  </ul>
                </div>
              )}

              <div className="mb-4 p-3 bg-slate-50 rounded text-sm">
                <p><strong>Total Rows:</strong> {panelExcelPreview.totalRows}</p>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">#</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Label</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Dimensions</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Location</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Panel Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">GL/MT Type</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-200">
                    {panelExcelPreview.rows.map((row, idx) => (
                      <tr key={idx}>
                        <td className="px-4 py-3 text-sm">{row.rowIndex + 1}</td>
                        <td className="px-4 py-3 text-sm font-medium">{row.label}</td>
                        <td className="px-4 py-3 text-sm">
                          {row.width_in && row.height_in ? `${row.width_in}" × ${row.height_in}"` : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm">{row.location}</td>
                        <td className="px-4 py-3 text-sm">
                          <Select
                            value={row.curtainwallTypeId || ''}
                            onValueChange={(value) => handleUpdatePanelPreviewRow(idx, 'curtainwallTypeId', value)}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue placeholder="Select..." />
                            </SelectTrigger>
                            <SelectContent>
                              {panelExcelPreview.cwTypes.map((cw) => (
                                <SelectItem key={cw.id} value={cw.id}>{cw.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <Select
                            value={row.panelType || ''}
                            onValueChange={(value) => handleUpdatePanelPreviewRow(idx, 'panelType', value)}
                          >
                            <SelectTrigger className="w-28">
                              <SelectValue placeholder="Type..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="GL">Glass</SelectItem>
                              <SelectItem value="MT">Metal</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {row.panelType === 'GL' && (
                            <Select
                              value={row.glassTypeId || ''}
                              onValueChange={(value) => handleUpdatePanelPreviewRow(idx, 'glassTypeId', value)}
                            >
                              <SelectTrigger className="w-28">
                                <SelectValue placeholder="Glass..." />
                              </SelectTrigger>
                              <SelectContent>
                                {panelExcelPreview.glassTypes.map((gl) => (
                                  <SelectItem key={gl.id} value={gl.id}>{gl.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          {row.panelType === 'MT' && (
                            <Select
                              value={row.metalTypeId || ''}
                              onValueChange={(value) => handleUpdatePanelPreviewRow(idx, 'metalTypeId', value)}
                            >
                              <SelectTrigger className="w-28">
                                <SelectValue placeholder="Metal..." />
                              </SelectTrigger>
                              <SelectContent>
                                {panelExcelPreview.metalTypes.map((mt) => (
                                  <SelectItem key={mt.id} value={mt.id}>{mt.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex gap-2 mt-4">
                <Button onClick={handlePanelBulkImport} className="flex-1">
                  Import Panels
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => {
                    setShowPanelExcelDialog(false);
                    setPanelExcelPreview(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </TabsContent>
  );
};

export default CWPanelListSection;
